import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Shield, Award, Clock, Star, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { pricingPlans, processSteps, countryCodesList } from '../mock';
import { toast } from 'sonner';
import { Check } from 'lucide-react';
import TestimonialSlider from '../components/TestimonialSlider';
import { fetchGeoLocation, submitLead, handleApiResponse } from '../services/apiService';

const LandingPage = ({ onOpenPopup }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    message: ''
  });
  const [geoData, setGeoData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadGeoData = async () => {
      const data = await fetchGeoLocation();
      setGeoData(data);
      if (data?.data?.countryCode) {
        const countryItem = countryCodesList.find(c => c.country === data.data.countryName);
        if (countryItem) {
          setFormData(prev => ({ ...prev, countryCode: countryItem.code }));
        }
      }
    };
    loadGeoData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await submitLead(formData, geoData);
      const result = handleApiResponse(response);
      
      if (result.type === 'redirect') {
        toast.success(result.message);
        navigate(result.url);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
      setFormData({ name: '', email: '', phone: '', countryCode: '+1', message: '' });
    }
  };

  const handleChatClick = () => {
    // Open Tawktrik chat widget
    if (window.openChatWidget) {
      window.openChatWidget();
    }
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Register Your Trademark Fast & Easy - Trademark Inspire | Starting at $45 + Filing Fee</title>
        <meta name="description" content="Fast, affordable trademark registration starting at $45. 98% success rate, expert guidance, comprehensive search included. Protect your business name, logo & brand identity today with Trademark Inspire!" />
        <meta name="keywords" content="trademark registration cost, fast trademark filing, affordable trademark service, register business name, protect logo, trademark packages, USPTO filing" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://mobile-scroll-test-1.preview.emergentagent.com/lp" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Register Your Trademark Fast & Easy - Trademark Inspire" />
        <meta property="og:description" content="Fast, affordable trademark registration starting at $45. 98% success rate, expert guidance. Protect your brand today!" />
        <meta property="og:url" content="https://mobile-scroll-test-1.preview.emergentagent.com/lp" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="Trademark Inspire" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Register Your Trademark Fast & Easy - Trademark Inspire" />
        <meta name="twitter:description" content="Fast, affordable trademark registration starting at $45. 98% success rate, expert guidance." />
        
        {/* Structured Data - JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "Trademark Inspire",
            "description": "Professional trademark registration and intellectual property services",
            "url": "https://mobile-scroll-test-1.preview.emergentagent.com/lp",
            "telephone": "+18882029517",
            "email": "Info@trademarkinspire.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "1652 W Texas St",
              "addressLocality": "Fairfield",
              "addressRegion": "CA",
              "postalCode": "94533",
              "addressCountry": "US"
            },
            "priceRange": "$$",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "10000"
            },
            "offers": {
              "@type": "Offer",
              "name": "Trademark Registration Service",
              "price": "45",
              "priceCurrency": "USD",
              "description": "Professional trademark registration starting at $45 plus USPTO filing fee"
            }
          })}
        </script>
        
        {/* Preconnect to external domains */}
        <link rel="preconnect" href="https://images.unsplash.com" />
        <link rel="dns-prefetch" href="https://images.unsplash.com" />
      </Helmet>
      {/* Hero Section with Form */}
      <section className="relative bg-gradient-to-br from-[#093336] via-[#0a4548] to-[#093336] text-white py-16 md:py-20 overflow-hidden">
        {/* Optimized background image with lazy loading */}
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0" 
            style={{ 
              backgroundImage: 'url(https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1920&q=60&auto=format)', 
              backgroundSize: 'cover', 
              backgroundPosition: 'center',
              willChange: 'transform'
            }}
          ></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center max-w-6xl mx-auto">
            {/* Left Side - Content */}
            <div className="text-center md:text-left">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 leading-tight">
                Protect Your Brand Legacy
              </h1>
              <p className="text-lg md:text-xl mb-6 text-gray-100">
                Register your trademark today and secure your business's most valuable asset. Expert guidance, transparent pricing, and proven results.
              </p>
              
              <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <CheckCircle size={20} className="text-[#F77F00] flex-shrink-0" />
                  <span className="text-base md:text-lg">98% Success Rate with USPTO Approvals</span>
                </div>
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <CheckCircle size={20} className="text-[#F77F00] flex-shrink-0" />
                  <span className="text-base md:text-lg">10,000+ Trademarks Successfully Registered</span>
                </div>
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <CheckCircle size={20} className="text-[#F77F00] flex-shrink-0" />
                  <span className="text-base md:text-lg">Expert Legal Team with 24/7 Support</span>
                </div>
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <CheckCircle size={20} className="text-[#F77F00] flex-shrink-0" />
                  <span className="text-base md:text-lg">Transparent Pricing - No Hidden Fees</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center md:justify-start">
                <Button
                  onClick={onOpenPopup}
                  className="bg-[#F77F00] hover:bg-[#e57300] text-white px-6 md:px-8 py-4 md:py-6 text-base md:text-lg font-semibold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                >
                  Get Started Now <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button
                  onClick={handleChatClick}
                  className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-2 border-white px-6 md:px-8 py-4 md:py-6 text-base md:text-lg font-semibold"
                >
                  Chat Now
                </Button>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-md mx-auto md:max-w-none w-full">
              <h2 className="text-2xl font-bold text-[#093336] mb-2">Register Your Trademark</h2>
              <p className="text-gray-600 mb-6">Get started today - fill out the form below</p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <Input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your full name"
                    className="bg-white text-gray-900"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your.email@example.com"
                    className="bg-white text-gray-900"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number *</label>
                  <div className="flex gap-2">
                    <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value })}>
                      <SelectTrigger className="w-[120px] bg-white text-gray-900">
                        <SelectValue placeholder="+1">
                          {formData.countryCode || "+1"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {countryCodesList.map((item) => (
                          <SelectItem key={item.id} value={item.code}>
                            {item.code} ({item.country})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                        setFormData({ ...formData, phone: value });
                      }}
                      placeholder="Phone number"
                      className="flex-1 bg-white text-gray-900"
                      pattern="[0-9]{10}"
                      title="Please enter a valid 10-digit phone number"
                      maxLength={10}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us about your trademark..."
                    rows={3}
                    className="bg-white text-gray-900"
                  />
                </div>
                
                <Button type="submit" disabled={isSubmitting} className="w-full bg-[#093336] hover:bg-[#0a4548] text-white py-6 text-lg font-semibold disabled:opacity-50">
                  {isSubmitting ? 'Submitting...' : 'Register My Trademark'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left side - Image */}
              <div className="order-2 md:order-1">
                <img 
                  src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=75&auto=format" 
                  alt="Professional Trademark Services" 
                  className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
                  loading="lazy"
                  width="800"
                  height="500"
                />
              </div>
              
              {/* Right side - Content */}
              <div className="order-1 md:order-2">
                <div className="inline-block mb-4">
                  <span className="bg-[#F77F00]/10 text-[#F77F00] px-4 py-2 rounded-full text-sm font-semibold border border-[#F77F00]/30">
                    Why Choose Us
                  </span>
                </div>
                <h2 className="text-4xl font-bold text-[#093336] mb-6">
                  Protecting Your Brand with Expert Trademark Services
                </h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  At Trademark Inspire, we understand that your trademark is more than just a symbol—it's the cornerstone of your business identity. With over a decade of experience, our dedicated team has successfully registered thousands of trademarks for businesses across the United States.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Our comprehensive services include thorough trademark searches, complete application guidance, and ongoing monitoring. With a 98% success rate and transparent pricing, we provide personalized attention and strategic guidance to ensure a smooth, stress-free experience from start to finish.
                </p>
                <div className="mt-8 flex gap-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={24} className="text-[#F77F00]" />
                    <span className="text-gray-700 font-medium">Free Consultation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={24} className="text-[#F77F00]" />
                    <span className="text-gray-700 font-medium">Fast Processing</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="md:grid md:grid-cols-4 gap-8 max-w-5xl mx-auto mobile-scroll-container md:flex-none">
            <div className="text-center mobile-scroll-item-small">
              <Shield size={40} className="mx-auto mb-3 text-[#093336]" />
              <div className="text-3xl font-bold text-[#093336] mb-1">10,000+</div>
              <div className="text-gray-600">Trademarks Protected</div>
            </div>
            <div className="text-center mobile-scroll-item-small">
              <Award size={40} className="mx-auto mb-3 text-[#093336]" />
              <div className="text-3xl font-bold text-[#093336] mb-1">98%</div>
              <div className="text-gray-600">Success Rate</div>
            </div>
            <div className="text-center mobile-scroll-item-small">
              <Clock size={40} className="mx-auto mb-3 text-[#093336]" />
              <div className="text-3xl font-bold text-[#093336] mb-1">24/7</div>
              <div className="text-gray-600">Expert Support</div>
            </div>
            <div className="text-center mobile-scroll-item-small">
              <Star size={40} className="mx-auto mb-3 text-[#093336]" />
              <div className="text-3xl font-bold text-[#093336] mb-1">5,000+</div>
              <div className="text-gray-600">Happy Clients</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#093336] mb-4">Why Choose Trademark Inspire?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide comprehensive trademark services backed by expertise and a commitment to your success.
            </p>
          </div>
          
          <div className="md:grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mobile-scroll-container md:flex-none">
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center mobile-scroll-item">
              <div className="w-16 h-16 bg-[#093336] text-white rounded-xl flex items-center justify-center mb-4 mx-auto">
                <CheckCircle size={32} />
              </div>
              <h3 className="text-xl font-bold text-[#093336] mb-3">Expert Guidance</h3>
              <p className="text-gray-600">Our experienced attorneys ensure your application is filed correctly the first time.</p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center mobile-scroll-item">
              <div className="w-16 h-16 bg-[#093336] text-white rounded-xl flex items-center justify-center mb-4 mx-auto">
                <CheckCircle size={32} />
              </div>
              <h3 className="text-xl font-bold text-[#093336] mb-3">Fast Processing</h3>
              <p className="text-gray-600">Quick turnaround times with dedicated case managers ensuring efficiency.</p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center mobile-scroll-item">
              <div className="w-16 h-16 bg-[#093336] text-white rounded-xl flex items-center justify-center mb-4 mx-auto">
                <CheckCircle size={32} />
              </div>
              <h3 className="text-xl font-bold text-[#093336] mb-3">Affordable Pricing</h3>
              <p className="text-gray-600">Transparent pricing with packages to fit every budget. No hidden fees.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Informative Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Right side - Image */}
              <div className="order-2 md:order-2">
                <img 
                  src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800&q=75&auto=format" 
                  alt="Professional Legal Team - Trademark Attorneys" 
                  className="rounded-2xl shadow-2xl w-full h-[450px] object-cover"
                  loading="lazy"
                  width="800"
                  height="450"
                />
              </div>
              
              {/* Left side - Content */}
              <div className="order-1 md:order-1">
                <div className="inline-block mb-4">
                  <span className="bg-[#F77F00]/10 text-[#F77F00] px-4 py-2 rounded-full text-sm font-semibold border border-[#F77F00]/30">
                    Our Commitment
                  </span>
                </div>
                <h2 className="text-4xl font-bold text-[#093336] mb-6">
                  Your Success is Our Priority
                </h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  We don't just file paperwork—we partner with you to protect your most valuable business asset. Our team of experienced trademark attorneys and specialists work tirelessly to ensure your brand receives the protection it deserves in today's competitive marketplace.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  With transparent pricing, dedicated case managers, and a proven track record of 98% success rate, we make trademark registration simple, affordable, and stress-free. Join thousands of satisfied clients who trust us to safeguard their brands.
                </p>
                <div className="mt-8 grid grid-cols-2 gap-6">
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <div className="text-3xl font-bold text-[#093336] mb-1">15+</div>
                    <div className="text-sm text-gray-600">Years Experience</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <div className="text-3xl font-bold text-[#093336] mb-1">50+</div>
                    <div className="text-sm text-gray-600">Expert Attorneys</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#093336] mb-4">Our Simple 3-Step Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We make trademark registration easy and straightforward
            </p>
          </div>
          
          <div className="md:grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mobile-scroll-container md:flex-none">
            {processSteps.map((step, index) => (
              <div key={step.id} className="relative mobile-scroll-item">
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg p-8 h-full">
                  <div className="w-20 h-20 bg-[#093336] text-white rounded-full flex items-center justify-center text-3xl font-bold mb-6 mx-auto">
                    {index + 1}
                  </div>
                  <h3 className="font-bold text-[#093336] mb-3 text-center text-xl">{step.title}</h3>
                  <p className="text-gray-600 text-center leading-relaxed">{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-14 -right-4 w-8 h-1 bg-[#F77F00]"></div>
                )}
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button
              onClick={onOpenPopup}
              className="bg-[#F77F00] hover:bg-[#e57300] text-white px-8 py-6 text-lg font-semibold"
            >
              Register My Trademark <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#093336] mb-4">Choose Your Package</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transparent pricing with no hidden fees. Select the package that best fits your needs.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {pricingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`bg-white rounded-2xl shadow-lg p-6 flex flex-col relative ${
                  plan.recommended ? 'border-4 border-[#F77F00] transform scale-105' : 'border border-gray-200'
                }`}
              >
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#F77F00] text-white px-4 py-1 rounded-full text-sm font-bold">
                    Recommended
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-[#093336] mb-2">{plan.name}</h3>
                  <div className="mb-2">
                    <span className="text-4xl font-bold text-[#093336]">{plan.price}</span>
                  </div>
                  <p className="text-sm text-gray-600">{plan.subtitle}</p>
                </div>
                
                <div className="flex-1 mb-6">
                  <ul className="space-y-3 max-h-[300px] overflow-y-auto">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="text-center text-xs text-gray-500 mb-4">
                  {plan.note}
                </div>
                
                <Button
                  onClick={onOpenPopup}
                  className={`w-full py-6 text-lg font-semibold ${
                    plan.recommended
                      ? 'bg-[#F77F00] hover:bg-[#e57300]'
                      : 'bg-[#093336] hover:bg-[#0a4548]'
                  }`}
                >
                  Register My Trademark
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner After Pricing */}
      <section className="py-16 bg-gradient-to-r from-[#F77F00] to-[#e57300] text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="text-center md:text-left">
                <h3 className="text-3xl md:text-4xl font-bold mb-4">
                  Still Have Questions?
                </h3>
                <p className="text-lg text-white/90 mb-6">
                  Our trademark specialists are here to help. Get a free consultation and learn how we can protect your brand today.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                  <Button
                    onClick={onOpenPopup}
                    className="bg-white text-[#F77F00] hover:bg-gray-100 px-8 py-6 text-lg font-semibold shadow-xl"
                  >
                    Get Free Consultation
                  </Button>
                  <Button
                    onClick={handleChatClick}
                    className="bg-[#093336] hover:bg-[#0a4548] text-white px-8 py-6 text-lg font-semibold"
                  >
                    Chat with Expert
                  </Button>
                </div>
              </div>
              <div className="hidden md:flex justify-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/20">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle size={24} className="text-[#F77F00]" />
                      </div>
                      <span className="text-lg font-semibold">No Hidden Fees</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle size={24} className="text-[#F77F00]" />
                      </div>
                      <span className="text-lg font-semibold">100% Money Back Guarantee</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle size={24} className="text-[#F77F00]" />
                      </div>
                      <span className="text-lg font-semibold">24/7 Customer Support</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialSlider />

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#093336] mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">
                Got questions? We've got answers. Find everything you need to know about our trademark services.
              </p>
            </div>
            
            <div className="space-y-6">
              <details className="group bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <summary className="flex justify-between items-center cursor-pointer p-6 hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-bold text-[#093336] pr-4">How long does the trademark registration process take?</h3>
                  <span className="text-[#F77F00] group-open:rotate-180 transition-transform text-2xl">+</span>
                </summary>
                <div className="px-6 pb-6 text-gray-700 leading-relaxed">
                  The trademark registration process typically takes 8-12 months from filing to approval. However, you'll receive a filing receipt within 24-48 hours, giving you "pending" status. Our team monitors your application throughout the entire process and handles any office actions or issues that arise.
                </div>
              </details>

              <details className="group bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <summary className="flex justify-between items-center cursor-pointer p-6 hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-bold text-[#093336] pr-4">What's included in your trademark registration service?</h3>
                  <span className="text-[#F77F00] group-open:rotate-180 transition-transform text-2xl">+</span>
                </summary>
                <div className="px-6 pb-6 text-gray-700 leading-relaxed">
                  Our service includes a comprehensive trademark search, professional filing with the USPTO, dedicated case manager, monitoring of your application, response to office actions, and ongoing support. We also provide guidance on trademark usage and protection strategies to maximize your brand's legal protection.
                </div>
              </details>

              <details className="group bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <summary className="flex justify-between items-center cursor-pointer p-6 hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-bold text-[#093336] pr-4">Do you guarantee trademark approval?</h3>
                  <span className="text-[#F77F00] group-open:rotate-180 transition-transform text-2xl">+</span>
                </summary>
                <div className="px-6 pb-6 text-gray-700 leading-relaxed">
                  While we maintain a 98% success rate and do everything possible to secure your trademark, no one can guarantee approval as the final decision rests with the USPTO. However, we offer a comprehensive pre-filing search and professional guidance to maximize your chances. If your application is denied due to our error, we offer a refund or refile guarantee.
                </div>
              </details>

              <details className="group bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <summary className="flex justify-between items-center cursor-pointer p-6 hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-bold text-[#093336] pr-4">How much does trademark registration cost?</h3>
                  <span className="text-[#F77F00] group-open:rotate-180 transition-transform text-2xl">+</span>
                </summary>
                <div className="px-6 pb-6 text-gray-700 leading-relaxed">
                  Our trademark registration packages start at $149 plus USPTO filing fees. We offer transparent pricing with no hidden fees. The total cost depends on the package you choose and the number of trademark classes you need to register. View our pricing section above for detailed package information.
                </div>
              </details>

              <details className="group bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <summary className="flex justify-between items-center cursor-pointer p-6 hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-bold text-[#093336] pr-4">Can I trademark a name that's similar to an existing trademark?</h3>
                  <span className="text-[#F77F00] group-open:rotate-180 transition-transform text-2xl">+</span>
                </summary>
                <div className="px-6 pb-6 text-gray-700 leading-relaxed">
                  It depends on several factors including the industry, likelihood of confusion, and how similar the marks are. Our comprehensive trademark search will identify any potential conflicts. If similar marks exist, our attorneys will advise you on the likelihood of approval and suggest alternatives if necessary. We're committed to protecting your investment and ensuring your trademark has the best chance of success.
                </div>
              </details>

              <details className="group bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <summary className="flex justify-between items-center cursor-pointer p-6 hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-bold text-[#093336] pr-4">What happens after my trademark is registered?</h3>
                  <span className="text-[#F77F00] group-open:rotate-180 transition-transform text-2xl">+</span>
                </summary>
                <div className="px-6 pb-6 text-gray-700 leading-relaxed">
                  Once registered, you'll receive your official trademark certificate. We provide guidance on proper trademark usage, renewal reminders (trademarks must be renewed every 10 years), and optional monitoring services to protect against infringement. You'll have full legal rights to use the ® symbol and can take legal action against infringers.
                </div>
              </details>
            </div>

            <div className="text-center mt-12">
              <p className="text-gray-600 mb-6">Still have questions?</p>
              <Button
                onClick={onOpenPopup}
                className="bg-[#093336] hover:bg-[#0a4548] text-white px-8 py-6 text-lg font-semibold"
              >
                Talk to a Trademark Specialist
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="relative py-24 bg-gradient-to-br from-[#093336] via-[#0a4548] to-[#093336] text-white overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#F77F00] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#F77F00] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-[#F77F00] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        {/* Decorative Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(247, 127, 0, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(247, 127, 0, 0.1) 1px, transparent 1px)', backgroundSize: '50px 50px' }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            {/* Main Content */}
            <div className="text-center mb-12">
              <div className="inline-block mb-6 animate-bounce">
                <div className="bg-[#F77F00] text-white px-6 py-3 rounded-full text-sm font-bold shadow-2xl">
                  ⚡ SPECIAL OFFER - LIMITED TIME ONLY
                </div>
              </div>
              
              <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Your Brand Deserves
                <span className="block text-[#F77F00] mt-2">Legal Protection Today!</span>
              </h2>
              
              <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
                Join 10,000+ successful businesses who trusted us to secure their trademarks. Don't risk your brand's future—start your protection now!
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
                <Button
                  onClick={onOpenPopup}
                  className="group bg-[#F77F00] hover:bg-[#e57300] text-white px-12 py-8 text-xl font-bold shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all rounded-xl"
                >
                  <span className="flex items-center justify-center gap-3">
                    Start My Application Now
                    <ArrowRight className="group-hover:translate-x-2 transition-transform" size={24} />
                  </span>
                </Button>
                <Button
                  onClick={handleChatClick}
                  className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-2 border-white/50 px-12 py-8 text-xl font-bold shadow-xl hover:shadow-2xl transition-all rounded-xl"
                >
                  Talk to Specialist
                </Button>
              </div>
              
              {/* Trust Badges */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto mb-8">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <Shield size={28} className="text-[#F77F00]" />
                    <span className="text-2xl font-bold">98%</span>
                  </div>
                  <p className="text-sm text-gray-300">Success Rate</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <Clock size={28} className="text-[#F77F00]" />
                    <span className="text-2xl font-bold">24/7</span>
                  </div>
                  <p className="text-sm text-gray-300">Expert Support</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <CheckCircle size={28} className="text-[#F77F00]" />
                    <span className="text-2xl font-bold">10K+</span>
                  </div>
                  <p className="text-sm text-gray-300">Trademarks Filed</p>
                </div>
              </div>
              
              {/* Bottom Trust Line */}
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-300">
                <div className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-[#F77F00]" />
                  <span>No Credit Card Required</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-[#F77F00]" />
                  <span>Free Consultation</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-[#F77F00]" />
                  <span>100% Money Back Guarantee</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
